import { PROTOCOL_VERSION, compareVersions } from '@il-colpo/protocol';
import { gameServerHttpUrl } from '../social/gameServerApi.ts';

export type VersionStatus = 'ok' | 'update-required';

/** Pure decision: the client and server must speak exactly the same protocol version. */
export function evaluateProtocol(serverVersion: number): VersionStatus {
  return serverVersion === PROTOCOL_VERSION ? 'ok' : 'update-required';
}

/** What the server says about itself: its protocol, and the oldest app it still accepts. */
export function evaluateServer(
  info: { protocol: number; minClient?: string | undefined },
  appVersion: string,
): VersionStatus {
  if (evaluateProtocol(info.protocol) === 'update-required') return 'update-required';
  return info.minClient !== undefined && compareVersions(appVersion, info.minClient) < 0
    ? 'update-required'
    : 'ok';
}

export type VersionCheckResult =
  | { status: VersionStatus; source: 'server' }
  /** No server is configured or reachable: offline modes still work, online ones will fail later. */
  | { status: 'ok'; source: 'skipped' };

/**
 * Asks the game server (`GET /version`) which protocol it speaks and which app versions it accepts. With
 * no server named in the build, or none that answers, the check is skipped: the offline modes do not need one.
 */
export async function checkServerVersion(
  restUrl: string | null | undefined = gameServerHttpUrl(),
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 2500,
  appVersion: string = __APP_VERSION__,
): Promise<VersionCheckResult> {
  if (!restUrl) return { status: 'ok', source: 'skipped' };
  try {
    const res = await fetchImpl(`${restUrl.replace(/\/$/, '')}/version`, {
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!res.ok) return { status: 'ok', source: 'skipped' };
    const body = (await res.json()) as { protocol?: unknown; minClient?: unknown };
    if (typeof body.protocol !== 'number') return { status: 'ok', source: 'skipped' };
    const minClient = typeof body.minClient === 'string' ? body.minClient : undefined;
    return {
      status: evaluateServer({ protocol: body.protocol, minClient }, appVersion),
      source: 'server',
    };
  } catch {
    return { status: 'ok', source: 'skipped' };
  }
}
